import { motion, useReducedMotion, type Variants } from 'framer-motion';
import type { ReactNode } from 'react';

/** How far a revealed element travels before settling. */
const RISE = 28;

/**
 * Bring a block in as it reaches the viewport.
 *
 * Everything on the page shares this one gesture — a short rise out of
 * transparency — so the page reads as one piece rather than a parade of
 * different effects. It plays once; scrolling back up doesn't replay it.
 *
 * Anyone who has asked their system to reduce motion gets the finished state
 * immediately, with no movement at all.
 */
export function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const still = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={still ? false : { opacity: 0, y: RISE }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2, margin: '0px 0px -80px 0px' }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/** A parent whose children rise one after another. Pair with `RevealItem`. */
export function RevealGroup({
  children,
  stagger = 0.12,
  className,
}: {
  children: ReactNode;
  stagger?: number;
  className?: string;
}) {
  const still = useReducedMotion();
  const variants: Variants = {
    hidden: {},
    shown: { transition: { staggerChildren: still ? 0 : stagger } },
  };
  return (
    <motion.div
      className={className}
      variants={variants}
      initial={still ? false : 'hidden'}
      whileInView="shown"
      viewport={{ once: true, amount: 0.15, margin: '0px 0px -80px 0px' }}
    >
      {children}
    </motion.div>
  );
}

export const riseItem: Variants = {
  hidden: { opacity: 0, y: RISE },
  shown: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
